import React, {useState} from 'react';

import cs from 'classnames';
import {browserHistory} from 'react-router';

import {CloneDocumentModal} from 'app/components/general/GenericDocumentActionButtons/CloneDocumentModal';
import {StyledCompactItemHeaderSpan} from 'app/general/bootstrap/utils';
import {DocumentPrivateStatusIcon} from 'app/general/doc-status/DocumentPrivateStatus';
import {DeleteDocumentModal} from 'app/general/modals/DeleteDocumentModal';
import {ActionsButton} from 'app/general/widgets/ActionsButton';
import {DashboardItemPresentMembers} from 'app/presence/components/DashboardItemPresentMembers';
import {StyledDocItem} from 'app/procedures/styles';
import {getKBEditPage} from 'app/urls';
import {getObjectName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';

import type {KnowledgebaseSerializedType} from 'sweet-types';

interface Props {
  kb: KnowledgebaseSerializedType;
  isSuperManager: boolean;
}

export function CompactKBListItem({kb, isSuperManager}: Props) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showingDuplicateModal, showDuplicateModal] = useState<boolean>(false);

  return (
    <>
      <StyledDocItem
        className="row border-top py-2 d-flex align-items-center"
        style={{position: 'relative'}}
      >
        <DashboardItemPresentMembers
          contentType="knowledgebase"
          objectId={kb.id}
        />
        <div className="col d-flex align-items-center">
          <a
            href={kb.enabled ? kb.html_url : undefined}
            className={cs('text-dark font-weight-bold d-block d-sm-inline')}
          >
            <StyledCompactItemHeaderSpan>
              <i className="far fa-book-open mr-1" />{' '}
              {getObjectName(kb.name, 'knowledgebase', true)}
              {!kb.enabled ? (
                <span className="ml-2 badge badge-light">{gt('disabled')}</span>
              ) : null}
            </StyledCompactItemHeaderSpan>
          </a>
        </div>
        <div className="col-auto">
          <DocumentPrivateStatusIcon
            className="ml-1"
            ct="knowledgebase"
            isPrivate={kb.private}
          />
        </div>

        {isSuperManager ? (
          <div className="col-auto order-lg-5">
            <ActionsButton
              actions={[
                {
                  key: 'edit',
                  icon: 'fa fa-edit',
                  text: gt('Edit'),
                  callback: () => browserHistory.push(getKBEditPage(kb.key)),
                },
                {
                  key: 'duplicate',
                  icon: 'far fa-copy',
                  text: gt('Duplicate'),
                  callback: () => showDuplicateModal(true),
                },
                {
                  key: 'delete',
                  icon: 'far fa-trash',
                  text: gt('Delete'),
                  callback: () => setShowDeleteModal(true),
                },
              ]}
            />
          </div>
        ) : null}
      </StyledDocItem>
      {showDeleteModal != null ? (
        <DeleteDocumentModal
          show={showDeleteModal}
          object={kb}
          redirectContentType={'knowledgebase'}
          onClose={() => setShowDeleteModal(false)}
        />
      ) : null}
      <CloneDocumentModal
        contentType={'knowledgebase'}
        documentTitle={kb.name || 'Untitled Knowledge Base'}
        documentURL={kb.url}
        isOpen={showingDuplicateModal}
        showFolder={false}
        showTeam={false}
        onClone={() => Promise.resolve()}
        onClose={() => showDuplicateModal(false)}
      />
    </>
  );
}
